import {
  ref,
  inject,
  h,
  defineComponent,
  PropType,
  renderSlot,
  computed,
  Ref,
} from "vue";
import { storeToRefs } from "pinia";
import { useI18nStore } from "@src/stores/I18nStore";
import _snakeCase from "lodash/snakeCase";
import _set from "lodash/set";
import _get from "lodash/get";

export function useI18n({
  locale = ref(inject("locale", "en")), // Inject active locale
  packs = [],
}: { locale?: Ref<string, string>; packs?: string[] } = {}) {
  // Use i18n store
  const i18nStore = useI18nStore();
  // Deconstruct i18n store
  const { messages } = storeToRefs(i18nStore);

  if (packs.length) loadLanguagePacks(packs);

  // Translate function

  function $t(path: string, object?: Record<string, string | undefined>) {
    return i18nStore.$t(locale.value, path, object);
  }

  function $tc(path: string, count: number, object?: Record<string, string>) {
    return i18nStore.$tc(locale.value, path, count, object);
  }

  function $raw(path: string, count?: number) {
    return i18nStore.$raw(locale.value, path, count);
  }

  function loadLanguagePacks(packs: string[] = []) {
    for (const pack of packs) {
      importPack(pack, locale.value).catch(() => {
        if (locale.value !== "en") {
          /* Try loading fallback (EN) pack */
          importPack(pack, "en", locale.value).catch((err) => err);
        }
      });
    }
  }

  async function importPack(pack: string, locale: string, storeAs?: string) {
    try {
      const storePath = `${storeAs || locale}.${_snakeCase(pack)}`;
      const storeData = _get(messages.value, storePath, {});
      if (!!Object.keys(storeData).length) return Promise.resolve();
      const { default: strings } = await import(
        `@src/i18n/${pack}/${locale}.json`
      );
      _set(messages.value, storePath, strings);
    } catch (error) {
      console.warn(`Language pack '${pack}/${locale}.json' not found.`);
      throw error;
    }
  }

  return {
    $t,
    $tc,
    $raw,
    loadLanguagePacks,
    locale,
    I18n: defineComponent({
      name: "I18n",
      props: {
        path: {
          type: String,
          required: true,
        },
        tag: {
          type: String,
          default: "span",
        },
        count: {
          type: Number,
          default: undefined,
        },
        values: {
          type: Object as PropType<Record<string, any>>,
          default: () => ({}),
        },
      },
      setup(props, { slots }) {
        const pattern = /\{([^}]+)\}/g;
        const rawText = computed(() => $raw(props.path, props.count) || "");
        const values = computed(() => ({
          n: `${props.count}`,
          count: `${props.count}`,
          ...props.values,
        }));

        const renderContent = (
          text: string
        ): Array<string | ReturnType<typeof renderSlot>> => {
          const parts: Array<string | ReturnType<typeof renderSlot>> = [];
          let match;
          let lastIndex = 0;

          while ((match = pattern.exec(text))) {
            const placeholder = match[1];
            const placeholderParts = placeholder.match(/^([^,]+),?(.+)?$/);
            const slotName = placeholderParts?.[1] || placeholder;
            const slotDefault = placeholderParts?.[2] || "";

            const slotContent = slots[slotName]?.() || "";
            const startIndex = match.index;
            // const endIndex = pattern.lastIndex - match[0].length;
            const beforeContent = text.slice(lastIndex, startIndex);
            lastIndex = pattern.lastIndex;

            if (beforeContent) parts.push(beforeContent);

            parts.push(
              slotContent
                ? renderSlot(slots, slotName, { content: slotContent })
                : (values.value as any)[slotName] || slotDefault
            );
          }

          const remainingContent = text.slice(lastIndex);

          if (remainingContent) parts.push(remainingContent);

          return parts;
        };

        return () => h(props.tag, {}, renderContent(rawText.value));
      },
    }),
  };
}
